import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'

import { DrawerBodyWrapper, DrawerFooterWrapper, Loader, TOAST_TYPE } from '@base'
import { Button as ButtonV2 } from '@baseV2'
import { enquiryInputType, strings } from '@constants'
import { editOrder, getAllBrandApi, getCategories, getProductByCategory } from '@data'
import { showLoader, showToast } from '@data/state/action'
import { toggleDrawer } from '@data/state/action/root'
import { generateItemId, isBrandOptional } from '@utils'
import EnquiryItemForm from '@pages/Enquiry/EnquiryItemForm'
import { getDuplicateGradeRowIds } from '@pages/Enquiry/EnquiryItemForm/inputAdapters'
import { Devices, Spacings } from '@styles'

const LoaderWrapper = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	min-height: 100%;
`
const ActionButton = styled(ButtonV2)`
	margin-left: ${Spacings.SPACING_4B};
	max-width: 14rem;
	flex-grow: 1;

	&:first-of-type {
		margin-left: 0;
	}

	@media ${Devices.tablet} {
		flex-grow: 0;
	}
`
const FormWrapper = styled.div`
	padding-top: ${Spacings.SPACING_4B};
	display: flex;
	flex-direction: column;
	gap: ${Spacings.SPACING_3B};
`
const initialFormState = {
	category: {
		label: strings('category'),
		disabled: false,
		readOnly: false,
		displayKey: 'category_name',
		primaryKey: 'category_id',
		placeholder: strings('select_a_category'),
		data: [],
		value: '',
		dirty: false,
		shouldFetchProducts: { flag: true },
	},
	product: {
		label: strings('product'),
		disabled: false,
		readOnly: false,
		displayKey: 'product_name',
		primaryKey: 'product_id',
		placeholder: strings('search_product'),
		data: [],
		value: '',
		dirty: false,
	},
	grade: {
		label: strings('grade'),
		placeholder: strings('enter', 'grade'),
		readOnly: false,
		value: '',
		dirty: false,
	},
	requiredWeight: {
		label: strings(['weight', '(', 'mt', ')']),
		disabled: false,
		readOnly: false,
		value: '',
		dirty: false,
	},
	targetPrice: {
		label: strings('target', 'price'),
		disabled: false,
		readOnly: false,
		value: '',
		dirty: false,
	},
	brands: {
		label: strings('brands'),
		disabled: false,
		readOnly: false,
		displayKey: 'name',
		primaryKey: 'id',
		placeholder: strings('select_brands'),
		data: [],
		value: [],
		dirty: false,
	},
	message: {
		label: strings('message'),
		disabled: false,
		readOnly: false,
		value: '',
		dirty: false,
	},
	tcRequired: {
		label: strings('tc_reqd'),
		disabled: false,
		value: false,
		dirty: false,
	},
	inputType: {
		label: strings('type'),
		disabled: false,
		value: '',
		dirty: false,
	},
	hsn_code: {
		label: strings('hsn', 'code'),
		placeholder: strings('enter', 'hsn', 'code'),
		readOnly: false,
		value: '',
		dirty: false,
	},
	gst: {
		label: strings('gst'),
		placeholder: strings('enter', 'gst'),
		readOnly: true,
		value: '',
		dirty: false,
	},
}

const AddOrderItem = ({ order, defualtCategoryId, update, supplierPO }) => {
	const buyer = order?.buyer_list?.[0]
	const supplier = order?.supplier_list?.find((s) => s.supplier_po_number === supplierPO)
	const tcKey = supplierPO ? 'tc_available' : 'tc_required'

	const orderItems = supplierPO ? supplier?.order_items : buyer?.order_items

	const dispatch = useDispatch()
	const user = useSelector((s) => s.getIn(['user', 'user']))
	const activeCommodity = user?.commodity?.find((c) => c?.active)
	const brandOptional = isBrandOptional(user)

	const [loading, setLoading] = useState(false)
	const [productsLoading, setProductsLoading] = useState(false)
	const [formState, setFormState] = useState(initialFormState)

	const lastCategoryProductsFetched = useRef()

	const checkTmtItem = ['ms_tmt_bars', 'scrap', 'lead_scrap']
	const defaultBrand = { name: 'ANY', id: 'any' }

	const updateFormState = useCallback((key, updates) => {
		setFormState((_oldState) => ({
			..._oldState,
			[key]: {
				..._oldState[key],
				...updates,
			},
		}))
	}, [])

	useEffect(() => {
		setLoading(true)
		const promises = [
			getCategories(0, 1000, order?.commodity?.commodity_id),
			getAllBrandApi(0, 1000, { commodityFilter: order?.commodity?.commodity_id }),
		]
		Promise.all(promises)
			.then(([categories, brands]) => {
				updateFormState('category', { data: categories.data })
				updateFormState('brands', {
					data: brands?.data?.map((brand) => ({
						name: brand?.brand_name,
						id: brand?.brand_id,
					})),
				})
			})
			.catch((err) => {
				console.log(err)
			})
			.finally(() => {
				setLoading(false)
			})
	}, [])

	useEffect(() => {
		if (formState.category.dirty) {
			updateFormState('product', { value: '', data: [], tmtProduct: undefined, dirty: false })
		}
		const categoryId = formState.category.value?.category_id
		if (categoryId && categoryId !== lastCategoryProductsFetched.current) {
			lastCategoryProductsFetched.current = categoryId
			setProductsLoading(true)
			getProductByCategory(categoryId)
				.then((data) => updateFormState('product', { data, value: '' }))
				.finally(() => setProductsLoading(false))
		}
	}, [formState.category.value, formState.category.shouldFetchProducts])

	const existingItemsForForm = useMemo(
		() =>
			(orderItems ?? []).map((it) => ({
				...it,
				required_weight: it.ordered_weight ?? it.allotted_weight,
				brands: it.brand_ordered ? [it.brand_ordered] : [],
				tc_required: it[tcKey],
			})),
		[orderItems, tcKey]
	)

	// Rows the user actually filled a weight against.
	const filledRows = useMemo(
		() => (formState?.product?.tmtProduct ?? []).filter((row) => row?.required_weight !== '' && row?.required_weight != null),
		[formState?.product?.tmtProduct]
	)

	const rowToOrderItem = (row, itemId) => {
		const enteredWeight = parseFloat(row.required_weight)
		const weight = formState.inputType.value === enquiryInputType.WEIGHT ? enteredWeight * 1000 : enteredWeight
		return {
			category_name: formState.category.value.category_name,
			category_id: formState.category.value.category_id,
			product_id: row.product_id,
			product_name: row.product_name,
			brand_ordered: formState.brands.value?.[0]?.name,
			hsn_code: formState.hsn_code.value,
			input_type: formState.inputType.value,
			[tcKey]: formState.tcRequired.value,
			price_per_kg: null,
			...(supplierPO ? { serviced_weight: null, allotted_weight: weight } : { ordered_weight: weight }),
			grade: row.grade === '' ? null : row.grade,
			item_id: itemId ?? generateItemId(row.product_id, row.grade),
			message: row.message ?? formState.message.value,
		}
	}

	const rowToOrderInvoiceItem = (row, itemId) => ({
		category_name: formState.category.value.category_name,
		category_id: formState.category.value.category_id,
		product_id: row.product_id,
		product_name: row.product_name,
		brand_ordered: formState.brands.value?.[0]?.name,
		serviced_weight: null,
		ordered_weight: null,
		item_id: itemId ?? generateItemId(row.product_id, row.grade),
		input_type: formState.inputType.value,
	})

	const duplicateCheck = (existingItems, newItems) => {
		if (!Array.isArray(existingItems)) return false

		const duplicateProductGrade = newItems.find((newItem) =>
			existingItems.some((existing) => existing.product_id === newItem.product_id && (existing.grade || null) === (newItem.grade || null))
		)

		if (duplicateProductGrade) {
			dispatch(showToast(true, strings('msg_item_already_added', [duplicateProductGrade?.product_name]), { type: TOAST_TYPE.ERROR }))
			return true
		}
		return false
	}

	const addNewItemHandler = () => {
		if (!filledRows.length) {
			dispatch(showToast(true, strings('msg_wrong_value'), { type: TOAST_TYPE.ERROR }))
			return
		}
		const seen = new Set()
		const newItems = []
		const newInvoiceItems = []
		filledRows.forEach((row) => {
			const itemId = generateItemId(row.product_id, row.grade)
			if (seen.has(itemId)) return
			seen.add(itemId)
			newItems.push(rowToOrderItem(row, itemId))
			newInvoiceItems.push(rowToOrderInvoiceItem(row, itemId))
		})

		const updatedOrder = { ...order }

		if (supplierPO) {
			const _supplier = updatedOrder.supplier_list.find((s) => s.supplier_po_number === supplierPO)
			const existing = _supplier.order_items
			const supplierInvoices = _supplier.supplier_invoices
			if (duplicateCheck(existing, newItems)) return
			_supplier.order_items = [...existing, ...newItems]

			if (supplierInvoices) {
				_supplier.supplier_invoices = supplierInvoices.map((invoice) => {
					if (!Array.isArray(invoice?.order_items)) return invoice
					return { ...invoice, order_items: [...invoice.order_items, ...newInvoiceItems] }
				})
			}
		} else {
			const _buyer = updatedOrder.buyer_list[0]
			const existing = _buyer.order_items
			const buyerInvoices = _buyer.buyer_invoices
			if (duplicateCheck(existing, newItems)) return
			_buyer.order_items = [...existing, ...newItems]

			if (buyerInvoices) {
				_buyer.buyer_invoices = buyerInvoices.map((invoice) => {
					if (!Array.isArray(invoice?.order_items)) return invoice
					return { ...invoice, order_items: [...invoice.order_items, ...newInvoiceItems] }
				})
			}
		}

		dispatch(showLoader(true))
		editOrder(updatedOrder)
			.then(() => {
				dispatch(showToast(true, strings('msg_order_editted'), { type: TOAST_TYPE.SUCCESS }))
				update()
				dispatch(toggleDrawer(false))
			})
			.catch((err) => {
				console.log(err)
				dispatch(showToast(true, 'Failed to add item. Try again', { type: TOAST_TYPE.ERROR }))
			})
			.finally(() => {
				dispatch(showLoader(false))
			})
	}

	// Same enable/disable rules as the enquiry add-item button.
	const addDisableButton = () => {
		// Brands are optional on the solar commodity, so they only gate the button elsewhere.
		if (!formState.category.value?.category_id) return true
		if (!brandOptional && !formState.brands.value.length) return true
		if (formState?.product?.tmtProduct?.length) {
			const reg = /^[\d]+(?:\.\d{1,3})?$/
			if (!filledRows.length) return true
			if (getDuplicateGradeRowIds(formState?.product?.tmtProduct).size) return true
			return !!filledRows.find((row) => !reg.test(row.required_weight))
		}
		if (formState?.product?.data.length && !formState?.product?.tmtProduct?.length) return true
		return false
	}

	return loading ? (
		<LoaderWrapper>
			<Loader />
		</LoaderWrapper>
	) : (
		<>
			<DrawerBodyWrapper>
				<FormWrapper>
					<EnquiryItemForm
						enquiryItems={existingItemsForForm}
						itemId={null}
						state={formState}
						updateState={updateFormState}
						productsLoading={productsLoading}
						defaultBrand={defaultBrand}
						defualtCategoryId={defualtCategoryId}
						checkTmtItem={checkTmtItem}
						activeCommodity={activeCommodity}
					/>
				</FormWrapper>
			</DrawerBodyWrapper>
			<DrawerFooterWrapper>
				<ActionButton color='primary' disabled={addDisableButton()} onClick={addNewItemHandler}>
					{strings('add_item')}
				</ActionButton>
			</DrawerFooterWrapper>
		</>
	)
}

export default AddOrderItem
